import { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  type ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import type { ExperimentConfig, SimulationState } from '@axlabs/simulation-core';
import styles from './GraphPanel.module.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface GraphPanelProps {
  config: ExperimentConfig;
  history: SimulationState[];
}

export function GraphPanel({ config, history }: GraphPanelProps) {
  // Determine which variables to plot based on experiment
  const plotConfig = useMemo(() => {
    switch (config.slug) {
      case 'simple-pendulum':
        return [
          { yVar: 'theta', label: 'θ (rad)', color: '#58a6ff' },
          { yVar: 'omega', label: 'ω (rad/s)', color: '#56d364' },
        ];
      case 'projectile-motion':
        return [
          { yVar: 'y', label: 'Height (m)', color: '#58a6ff' },
          { yVar: 'x', label: 'Range (m)', color: '#f0883e' },
        ];
      case 'shm-spring':
        return [
          { yVar: 'x', label: 'Position (m)', color: '#58a6ff' },
          { yVar: 'v', label: 'Velocity (m/s)', color: '#56d364' },
        ];
      case 'ohms-law':
        return [
          { yVar: 'current', label: 'Current (A)', color: '#58a6ff' },
          { yVar: 'power', label: 'Power (W)', color: '#f0883e' },
        ];
      case 'enzyme-kinetics':
        return [
          { yVar: 'substrateConc', label: '[S] (mM)', color: '#58a6ff' },
          { yVar: 'productConc', label: '[P] (mM)', color: '#56d364' },
        ];
      case 'population-dynamics':
        return [
          { yVar: 'prey', label: 'Prey', color: '#58a6ff' },
          { yVar: 'predator', label: 'Predator', color: '#f85149' },
        ];
      case 'chemical-equilibrium':
        return [
          { yVar: 'concA', label: '[A] (M)', color: '#58a6ff' },
          { yVar: 'concC', label: '[C] (M)', color: '#56d364' },
        ];
      default:
        // Default: plot first two numeric variables
        const vars = Object.keys(history[0]?.variables ?? {}).slice(0, 2);
        return vars.map((v, i) => ({
          yVar: v,
          label: v,
          color: i === 0 ? '#58a6ff' : '#56d364',
        }));
    }
  }, [config.slug, history]);
  
  // Sample data to prevent too many points
  const sampledHistory = useMemo(() => {
    if (history.length <= 200) return history;
    const step = Math.ceil(history.length / 200);
    return history.filter((_, i) => i % step === 0 || i === history.length - 1);
  }, [history]);
  
  const chartData = useMemo(() => ({
    labels: sampledHistory.map(s => s.time.toFixed(2)),
    datasets: plotConfig.map(pc => ({
      label: pc.label,
      data: sampledHistory.map(s => s.variables[pc.yVar] ?? 0),
      borderColor: pc.color,
      backgroundColor: pc.color + '33',
      borderWidth: 1.5,
      pointRadius: 0,
      tension: 0.1,
    })),
  }), [sampledHistory, plotConfig]);
  
  const options: ChartOptions<'line'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false, // Disable animation for real-time updates
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: '#8b949e',
          font: {
            family: 'Inter, sans-serif',
            size: 11,
          },
          boxWidth: 12,
          boxHeight: 12,
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: '#21262d',
        titleColor: '#e6edf3',
        bodyColor: '#8b949e',
        borderColor: '#30363d',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        type: 'category',
        title: {
          display: true,
          text: 'Time (s)',
          color: '#8b949e',
          font: {
            family: 'Inter, sans-serif',
            size: 11,
          },
        },
        ticks: {
          color: '#6e7681',
          font: {
            family: 'JetBrains Mono, monospace',
            size: 10,
          },
          maxTicksLimit: 6,
        },
        grid: {
          color: '#21262d',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Value',
          color: '#8b949e',
          font: {
            family: 'Inter, sans-serif',
            size: 11,
          },
        },
        ticks: {
          color: '#6e7681',
          font: {
            family: 'JetBrains Mono, monospace',
            size: 10,
          },
        },
        grid: {
          color: '#21262d',
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
  }), []);
  
  if (history.length === 0) {
    return (
      <div className={styles.empty}>
        <p>Run the simulation to see graphs</p>
      </div>
    );
  }
  
  return (
    <div className={styles.container}>
      <Line data={chartData} options={options} />
    </div>
  );
}
